import {
  skipToken,
  useIsMutating,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';

import { fetchClient } from '@/lib/openapi-fetch/fetchClient';

import { hasPendingReply } from './chatMessages';
import { chatEndpoints, PENDING_REPLY_POLL_MS } from './constants';
import { chatMutationKeys, chatQueryKeys } from './queryKeys';
import { refreshJobData } from './refreshJobData';
import type { ChatApi } from './types';
import type { SendChatMessageVariables } from './useSendChatMessageMutation';

/**
 * One of the user's chats. While a reply is pending it checks again every couple of seconds: the turn keeps running
 * on the server when the tab that sent it closes or its request is cut, and another tab may be the one waiting.
 *
 * It doesn't check while this tab's own message is being sent: the send's answer brings the chat back, and a check
 * meanwhile could show the chat without the message, which finlay-api saves only once another tab's turn is done.
 */
export const useChatQuery = (chatId: string | undefined) => {
  const queryClient = useQueryClient();
  const sending =
    useIsMutating({
      mutationKey: chatMutationKeys.send,
      predicate: (mutation) =>
        (mutation.state.variables as SendChatMessageVariables | undefined)
          ?.chatId === chatId,
    }) > 0;

  return useQuery({
    queryKey: chatQueryKeys.detail(chatId ?? ''),
    queryFn: chatId
      ? async () => {
          const { data } = await fetchClient.GET(chatEndpoints.chat, {
            params: { path: { id: chatId } },
          });
          // A reply that finished since the last check, such as another tab's, may have changed jobs or candidates.
          const before = queryClient.getQueryData<ChatApi>(
            chatQueryKeys.detail(chatId),
          );
          if (hasPendingReply(before) && !hasPendingReply(data)) {
            void refreshJobData(queryClient);
          }
          return data;
        }
      : skipToken,
    refetchInterval: (query) =>
      !sending && hasPendingReply(query.state.data)
        ? PENDING_REPLY_POLL_MS
        : false,
  });
};
